
import Interface from './interface';
import Polygon from './polygon';
import Intersection from './intersection';

const highlightColor = '#808080';
const defaultColor = '#404040';
const rad2deg = 57.29578049;

export default class Part extends Polygon {
  constructor (id, path, inner, source, offset = 10.0, rotation = 0) {
    // CAUTION: invert y coordinate to set left bottom as origin point    
    let maxY = Number.MIN_SAFE_INTEGER;
    path.map(p => {
      maxY = Math.max(maxY, p.y);
    });
    const points = path.map(p => {
      return new THREE.Vector2(p.x, maxY - p.y);
    });

    super(points, {
      fill: defaultColor
    });

    this.id = id;
    this.maxY = maxY;
    this.source = source; // original path json

    // Keep prev transform for history
    this.prevPosition = new THREE.Vector2();
    this.prevRotation = 0;
    this.rotation = rotation;

    this.prevMousePosition = { x: 0, y: 0 };
    this.selected = false;
    this.intersected = false;

    this.element.classList.add('part');
    this.element.addEventListener('mouseover', this.onMouseOver.bind(this));
    this.element.addEventListener('mouseout', this.onMouseOut.bind(this));
    this.element.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.element.addEventListener('mouseup', this.onMouseUp.bind(this));

    // inner
    // CAUTION: invert y coordinate to set left bottom as origin point
    this.innerPoints = inner.map(ipath => {
      return ipath.map(p => {
        return new THREE.Vector2(p.x, maxY - p.y);
      });
    });

    this.innerPolylines = this.innerPoints.map(ipath => {
      let poly = this.createPolylineSVG({
        strokeColor: '#333',
        strokeWidth: 2
      });
      poly.classList.add('part-inner'); //part-inner
      this.element.appendChild(poly);
      return poly;
    });

    //console.log("innerPoints; ", this.innerPoints);
    // offset - Intersection error
    this.offsetPoints = this.offset(this.points, offset);

    this.innerOffsetPoints = [];
    if (this.innerPoints)
    {

      this.innerOffsetPoints = this.innerPoints.map(ipath => 
        this.offset(ipath, -offset)
      );
    }

    let eps = 0.75;
    // intersection error here
    this.offsetCheckPoints = this.offset(this.points, offset- eps);

    this.innerOffsetCheckPoints = [];
    if (this.innerPoints)
    {

      // intersection error here
      this.innerOffsetCheckPoints = this.innerPoints.map(ipath => 
        this.offset(ipath, -offset+eps)
      )
    }  

    this.offsetPolyline = this.createPolylineSVG({
      strokeColor: '#333',
      strokeWidth: 1
    });
    this.offsetPolyline.classList.add('part-offset');
    this.element.appendChild(this.offsetPolyline);

    //Create offsetInnerPolylines  
    this.offsetInnerPolylines = this.innerOffsetPoints.map(ipath => {
      let poly = this.createPolylineSVG({
        strokeColor: '#333', // Slightly different color for offset inner paths
        strokeWidth: 1
      });
      poly.classList.add('part-offset-inner');      
      this.element.appendChild(poly);
      return poly;
    });

    const c = this.center(this.points);
    this.rotationPivot = this.createRotationPivot(c);
    this.element.appendChild(this.rotationPivot);

    this.rotationPivot.addEventListener('mouseover', (e) => {
      this.markRotationReady();
    });
    this.rotationPivot.addEventListener('mouseout', (e) => {
      this.unmarkRotationReady();
    });
    this.rotationPivot.addEventListener('mousedown', (e) => {
      this.emit('rotate', this, this.rotationPivot);
      e.preventDefault();
      e.stopPropagation();
      return false;
    });

    this.rotationLabel = this.createRotationLabel(c);
    this.element.appendChild(this.rotationLabel);
  }

  logBounds(points) {
    // Debug 用
    let maxX = Number.MIN_SAFE_INTEGER;
    let minX = Number.MAX_SAFE_INTEGER;
    let minY = Number.MAX_SAFE_INTEGER;
    let maxY = Number.MIN_SAFE_INTEGER;
    points.forEach((p) => {
      maxX = Math.max(maxX, p.x)
      minX = Math.min(minX, p.x)
      maxY = Math.max(maxY, p.y)
      minY = Math.min(minY, p.y)
    })
    console.log(maxX - minX, maxY - minY);
  }

  update () {
    super.update();
    const center = this.center(this.points);
    this.updatePolyline(this.offsetPolyline, this.offsetCheckPoints, center);
    this.innerPolylines.forEach((poly, idx) => {
      this.updatePolyline(poly, this.innerPoints[idx], center);
    });

    if (this.offsetInnerPolylines) {
      this.offsetInnerPolylines.forEach((poly, idx) => {
        if (this.innerOffsetPoints[idx]) { // ✅ Ensure it's not null or undefined
          this.updatePolyline(poly, this.innerOffsetPoints[idx], center);
        }
      });
    }

    

    const c = center.add(this.position);
    this.updateCircle(this.rotationPivot, c);
    this.updateText(this.rotationLabel, c);
  }

  select () {
    this.selected = true;
    this.polyline.classList.add('selected');
  }

  unselect () {
    this.selected = false;
    this.polyline.classList.remove('selected');
    this.unmarkRotation();
  }

  highlight () {
    this.polyline.classList.add('highlighted');
  }

  unhighlight () {
    this.polyline.classList.remove('highlighted');
  }

  markIntersected () {
    this.polyline.classList.add('intersected');
    this.intersected = true;
  }

  unmarkIntersected () {
    this.polyline.classList.remove('intersected');
    this.intersected = false;
  }

  markRotation () {
    this.element.classList.add('rotating');
  }

  unmarkRotation () {
    this.element.classList.remove('rotating');
  }

  markRotationReady () {
    this.element.classList.add('rotation-ready');
  }

  unmarkRotationReady () {
    this.element.classList.remove('rotation-ready');
  }

  onMouseOver (e) {
    if (e.which === 0) { this.highlight(); }
  }

  onMouseOut (e) {
    this.unhighlight();
  }

  onMouseDown (e) {
    this.emit('select', e, this);
  }

  onMouseUp (e) {
    this.emit('unselect', e, this);
  }

  includeElement (path, target) {
    for (let i = 0, n = path.length; i < n; i++) {
      let el = path[i];
      if (el === target) {
        return true;
      }
    }
    return false;
  }

  keep () {
    this.prevPosition.x = this.position.x;
    this.prevPosition.y = this.position.y;
    this.prevRotation = this.rotation;
  }

  prepareDrag (e) {
    this.keep();

    let p = Interface.getMousePosition(e);
    this.prevMousePosition.x = p.x;
    this.prevMousePosition.y = p.y;
  }

  drag (e, parent) {
    let p = Interface.getMousePosition(e);
    this.move((p.x - this.prevMousePosition.x) / parent.scale, (p.y - this.prevMousePosition.y) / parent.scale);
    this.prevMousePosition.x = p.x;
    this.prevMousePosition.y = p.y;
  }

  prepareRotate (e) {
    this.keep();
    this.markRotation();
  }

  rotateTo (r) {
    super.rotateTo(r);
    this.updateRotationText(this.rotation);
  }

  rotate (dr) {
    this.rotation = this.prevRotation + dr;
    this.updateRotationText(this.rotation);
    this.update();
  }

  copyTransform (source) {
    this.prevRotation = source.prevRotation;
    this.rotation = source.rotation;
    this.prevPosition.copy(source.prevPosition);
    this.position.copy(source.position);
    this.update();
  }

  // CAUTION: invert y coordinate to set left bottom as origin point
  // source: path data from path json (this.source)
  convert (bin, source, center, offset) {
    // return super.transform(source.map(p => new THREE.Vector2(p.x, -p.y)), center).map(p => {
    const s = source.map(p => new THREE.Vector2(p.x, this.maxY - p.y));
    return super.transform(s, center).map(p => {
      return {
        x: p.x - offset.x,
        y: bin.depth - (p.y - offset.y)
      };
    });
  }

  build (bin) {
    const center = this.center(this.points);
    const source = JSON.parse(JSON.stringify(this.source));
    const o = bin.position;
    source.ex_path = this.convert(bin, source.ex_path, center, o);
    source.in_paths = source.in_paths.map(inPath => this.convert(bin, inPath, center, o));
    source.pocket = source.pocket.map(pocket => {
      return {
        ex_pocket: this.convert(bin, pocket.ex_pocket, center, o),
        in_pocket: pocket.in_pocket.map(inPocket => this.convert(bin, inPocket, center, o))
      };
    });
    if (source.single_3d) {
      source.single_3d = source.single_3d.map(single3d => {
        const vectorArray = this.convert(bin, single3d, center, o);
        for(let i = 0; i < vectorArray.length; i++) {
          vectorArray[i].z = single3d[i].z;
        }
        return vectorArray;
      });
    }
    source.single_paths = source.single_paths.map(singlePath => this.convert(bin, singlePath, center, o))
    return source;
  }

  transform (points, center = undefined) {
    if (center === undefined) {
      // 元々　this.OffsetPoints でチェックするが this.offsetCheckPoints に変更
      if (points === this.offsetCheckPoints) { center = this.center(this.points); } else { center = this.center(points); }
    }
    return super.transform(points, center);
  }

  transformInner (points, innerPoints, center = undefined) {
    if (center === undefined) {
      // 元々　this.OffsetPoints でチェックするが this.offsetCheckPoints に変更
      if (innerPoints === this.innerOffsetCheckPoints) { center = this.center(this.points); } else { center = this.center(points); }
    }
    return super.transform(innerPoints, center);
  }

  intersects (target) {
    //　この部材とtargetの部材が重なっているかどうかをチェックする
    let points0 = [];
    let points0Inner = [];
    let points1 = [];
    let points1Inner = [];

    // 元々　this.OffsetPoints でチェックするが this.offsetCheckPoints に変更
    if (target instanceof Part) {
      points0 = this.transform(this.offsetCheckPoints);
      if (this.innerOffsetCheckPoints)
      {
        points0Inner = this.innerOffsetCheckPoints.map(innerPt => {
          if (innerPt)
          {
            return this.transformInner(this.offsetCheckPoints, innerPt);
          }
          return null;
        }).filter(innerPath => innerPath !== null); // Filter out null values
      }

      points1 = target.transform(target.offsetCheckPoints);
      if (target.innerOffsetCheckPoints)
      {
        points1Inner = target.innerOffsetCheckPoints.map(innerPt => {
          if (innerPt)
          {
            return this.transformInner(target.offsetCheckPoints, innerPt);
          }
          return null;
        }).filter(innerPath => innerPath !== null); // Filter out null values
      }

    } else {
      points0 = this.transform(this.offsetCheckPoints);
      if (this.innerOffsetCheckPoints)
      {
        points0Inner = this.innerOffsetCheckPoints.map(innerPt => {
          if (innerPt)
          {
            return this.transformInner(this.offsetCheckPoints, innerPt);
          }
          return null;
        }).filter(innerPath => innerPath !== null); // Filter out null values
      }

      points1 = target.transform(target.points);
      if (target.innerPoints)
      {
        points1Inner = target.innerPoints.map(innerPt => {
          if (innerPt)
          {
            return this.transformInner(target.points, innerPt);
          }
          return null;
        }).filter(innerPath => innerPath !== null); // Filter out null values
      }

    }

    // Check if any of the points of points1 are inside any of the inner holes (points0Inner)
    
    let allPoints1InsidePoints0Holes = true;
    for (let i = 0; i < points0Inner.length; i++) {
      const innerPath = points0Inner[i];
      for (let j = 0; j < points1.length; j++) {
        const point = points1[j];

        if (innerPath && !Intersection.contains(innerPath, point)) {
          // If the point is not inside the inner hole, the polygon is not inside the inner hole, this check is over,

          allPoints1InsidePoints0Holes = false;
          break;
        }
      }
      if (!allPoints1InsidePoints0Holes) break;
    }
    // If all points of points1 are inside the inner holes, return false
    if (allPoints1InsidePoints0Holes && points0Inner.length > 0) {
      return false;
    }

    let allPoints0InsidePoint1Holes = true;
    for (let i = 0; i < points1Inner.length; i++) {
      const innerPath = points1Inner[i];
      for (let j = 0; j < points0.length; j++) {
        const point = points0[j];
        if (innerPath && !Intersection.contains(innerPath, point)) {
          // If the point is not inside the inner hole, the polygon is not inside the inner hole, this check is over,
          allPoints0InsidePoint1Holes = false;
          break;
        }
      }
      if (!allPoints0InsidePoint1Holes) break;
    }

    if (allPoints0InsidePoint1Holes && points1Inner.length > 0) {
      return false;
    }


    const found0 = points0.find(p => Intersection.contains(points1, p));
    if (found0 !== undefined) {
      return true;
    }
    const found1 = points1.find(p => Intersection.contains(points0, p));
    if (found1 !== undefined) {
      return true;
    }
    return Intersection.intersects(points0, points1);
  }

  updateCircle (circle, center) {
    circle.setAttribute('cx', `${center.x}`);
    circle.setAttribute('cy', `${center.y}`);
  }

  updateRotationText (rotation) {
    let angle = Math.round(rotation * rad2deg);
    if (angle < 0) { angle = 360 + angle; }
    this.rotationLabel.textContent = `${angle}°`;
  }

  updateText (text, center) {
    text.setAttribute('x', `${center.x}`);
    text.setAttribute('y', `${center.y}`);
  }

  createRotationPivot (center, options = {}) {
    let circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('stroke', options.strokeColor || '#000000');
    circle.setAttribute('stroke-width', options.strokeWidth || 2);
    this.updateCircle(circle, center);
    circle.setAttribute('r', '8');
    return circle;
  }

  createRotationLabel (center) {
    let text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.textContent = '0°';
    this.updateText(text, center);
    return text;
  }

  serialize () {
    return {
      id: this.id,
      position: this.position,
      rotation: this.rotation
    };
  }

  deserialize (source) {
    const { id, position, rotation } = source;
    if (id !== this.id) return;

    const { x, y } = position;
    this.position.set(x, y);
    this.rotation = rotation;
    this.update();
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/nesting/part.js